"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"

import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  className?: string
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = "Pick a date",
  className,
  disabled,
  ...props
}: DatePickerProps & { disabled?: (date: Date) => boolean } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">) {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date)
  const [isOpen, setIsOpen] = React.useState(false)

  React.useEffect(() => {
    setSelectedDate(date)
  }, [date])

  const handleSelect = (newDate: Date | undefined) => {
    setSelectedDate(newDate)
    onDateChange?.(newDate)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-12 w-full items-center rounded-lg border border-border bg-input px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(0,0,0,0.1)] outline-none ring-0 transition-[box-shadow] duration-150 focus:border-white/30 focus:ring-[3px] focus:ring-white/10 focus:shadow-[inset_0_2px_4px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(0,0,0,0.1)] disabled:cursor-not-allowed disabled:opacity-50",
            !selectedDate && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <CalendarIcon className="mr-3 h-4 w-4 opacity-50" />
          {selectedDate ? format(selectedDate, "PPP") : <span>{placeholder}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-card border border-white/[0.08]"
        align="start"
        sideOffset={8}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          initialFocus
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  )
}
